import React from 'react';
import Webcam from 'react-webcam';
import { getDownloadURL } from 'firebase/storage';
import { PhotoCameraProps } from '../types/Camera/PhotoCameraProps';
import { FileUpload } from '../types/Files/FileUpload';
import { uploadFile } from '../data/uploadFile';
import { dataURItoBlob } from '../data/dataURItoBlob';
import { getAspectRatio } from '../data/getAspectRatio';
import { useHandleFileUploadSuccess } from '../hooks/useHandleFileUploadSuccess';
import { useMeasure } from '@uidotdev/usehooks';
import { useOrientation } from '@uidotdev/usehooks';
import { Button } from './buttons';
import * as Sentry from '@sentry/browser';

export const PhotoCamera = (props: PhotoCameraProps) => {
  const [outlineRef, { width, height }] = useMeasure();
  const [currentDevice, setCurrentDevice] = React.useState<MediaDeviceInfo | null>(null);
  const { working, setWorking } = props;

  const orientation = useOrientation();
  // SET CAMERA
  React.useEffect(() => {
    if (!currentDevice) {
      navigator.mediaDevices &&
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            // REDUCE TO VIDEO INPUT DEVICES ONLY
            devices = devices.filter((device) => device.kind === 'videoinput');

            // HANDLE CASE WHERE PERMISSION IS DENIED OR NO DEVICES ARE AVAILABLE
            const noPermissions = devices.find((device) => device.kind === 'videoinput' && device.label === '');
            if (noPermissions) {
              navigator.mediaDevices
                .getUserMedia({ video: true })
                .then((stream) => {
                  // STOP ALL TRACKS TO RELEASE CAMERA
                  stream.getTracks().forEach((track) => track.stop());
                  // RE-ENUMERATE DEVICES AFTER PERMISSIONS ARE GRANTED
                  navigator.mediaDevices.enumerateDevices().then((devices) => {
                    devices = devices.filter((device) => device.kind === 'videoinput');
                    if (devices.length > 0) {
                      let defaultDevice =
                        props.style === 'portrait'
                          ? devices.find((device) => device.label.toLowerCase().includes('front'))
                          : devices.find((device) => device.label.toLowerCase().includes('back'));

                      // IF NO DEVICE FOUND (LIKELY ON DESKTOP), SET THE FIRST AVAILABLE DEVICE AS DEFAULT
                      if (!defaultDevice) {
                        defaultDevice = devices[0];
                      }

                      if (defaultDevice) {
                        setCurrentDevice(defaultDevice);
                      }
                    }
                  });
                })
                .catch(() => {});
            } else {
              // SET THE CURRENT CAMERA BASED ON FACING MODE FOR MOBILE DEVICES
              if (devices.length > 0) {
                let defaultDevice =
                  props.style === 'portrait'
                    ? devices.find((device) => device.label.toLowerCase().includes('front'))
                    : devices.find((device) => device.label.toLowerCase().includes('back'));

                // IF NO DEVICE FOUND (LIKELY ON DESKTOP), SET THE FIRST AVAILABLE DEVICE AS DEFAULT
                if (!defaultDevice) {
                  defaultDevice = devices[0];
                }

                if (defaultDevice) {
                  setCurrentDevice(defaultDevice);
                }
              }
            }
          })
          .catch((error) => {
            Sentry.captureException(error);
            console.error('Error accessing media devices:', error);
            alert('Unable to access camera. Please check your device settings and permissions.');
          });
    } else {
      console.info('Current device set:', currentDevice);
    }
  }, [currentDevice, props.style]);

  const cardVideoConstraints = {
    width: { ideal: orientation.type.includes('portrait') ? 2160 : 3840 },
    height: { ideal: orientation.type.includes('portrait') ? 3840 : 2160 },
    // facingMode: props.style !== 'portrait' ? 'environment' : 'user',  // REPLACED BY DEVICEID APPROACH AS FACING MODE IS NOT COMPATIBLE WITH MOST SAMSUNG DEVICES
    deviceId: currentDevice?.deviceId,
  };

  const defaultVideoConstraints = {
    aspectRatio: getAspectRatio({
      style: props.style,
      orientation: orientation.type,
    }),
    // facingMode: props.style !== 'portrait' ? 'environment' : 'user', // REPLACED BY DEVICEID APPROACH AS FACING MODE IS NOT COMPATIBLE WITH MOST SAMSUNG DEVICES
    deviceId: currentDevice?.deviceId,
  };

  const webcamRef = React.useRef<Webcam>(null);

  const { updateFirestore } = useHandleFileUploadSuccess();

  const handleImageUpload = (imageSrc: string) => {
    const file = dataURItoBlob(imageSrc);
    const payload = {
      bucket: props.bucket,
      path: props.path,
      file,
    } as FileUpload;
    const uploadTask = uploadFile(payload);

    if (!uploadTask) {
      Sentry.captureMessage('Unable to create upload task');
      console.error('Unable to create upload task');
      props.hideCamera();
      alert(
        'Unable to connect to upload server. This is likely due to your current network quality or configuration. Please try again from your personal network without a VPN. If this problem persists, please notify support.',
      );
      return;
    }

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.info('Upload is ' + progress + '% done');
      },
      (error) => {
        Sentry.captureException(error);
        console.error(error);
        alert(`Error uploading file: ${error.message}`);
      },
      () => {
        // UPLOAD SUCCESSFUL
        getDownloadURL(uploadTask.snapshot.ref)
          .then((downloadURL) => {
            // SAVE THE DOWNLOAD URL TO FIRESTORE
            if (downloadURL) {
              const payload = {
                docToUpdate: props.docToUpdate,
                patientId: props.patientId,
                type: props.type,
                url: downloadURL,
              };
              updateFirestore(payload);
              props.hideCamera();
            }
          })
          .catch((error) => {
            Sentry.captureException(error);
            console.error('Error getting download URL:', error);
          });
      },
    );
  };
  const capture = React.useCallback(() => {
    setWorking(true);
    if (webcamRef.current) {
      // PASS OPTIONAL DIMENSIONS OBJECT TO AVOID SAVING LOW-RES IMAGES ON MOBILE (BYPASSES SCREEN CANVAS)
      // const imageSrc = webcamRef.current.getScreenshot({ width: 3840, height: 2160 });
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return;
      handleImageUpload(imageSrc);
    }
  }, [webcamRef]);

  const getCaptureBoxWidth = () => {
    if (width && orientation.type.includes('portrait')) {
      return `${width * 0.8}px`;
    } else if (width && orientation.type.includes('landscape')) {
      return `${width * 0.7}px`;
    }
    return '0px';
  };
  const captureBoxWidth = getCaptureBoxWidth();

  const getCaptureBoxHeight = () => {
    if (height && orientation.type.includes('portrait')) {
      return `${height * 0.7}px`;
    } else if (height && orientation.type.includes('landscape')) {
      return `${height * 0.8}px`;
    }
    return '0px';
  };
  const captureBoxHeight = getCaptureBoxHeight();

  const getCaptureBoxMarginTop = () => {
    if (width && orientation.type.includes('portrait')) {
      return `9%`;
    } else if (width && orientation.type.includes('landscape')) {
      return `5%`;
    }
    return '0%';
  };
  const captureBoxMarginTop = getCaptureBoxMarginTop();
  const getCaptureBoxMarginLeft = () => {
    if (width && orientation.type.includes('portrait')) {
      return `8%`;
    } else if (width && orientation.type.includes('landscape')) {
      return `13%`;
    }
    return '0px';
  };
  const captureBoxMarginLeft = getCaptureBoxMarginLeft();

  if (!currentDevice || !currentDevice.deviceId) {
    return 'Accessing camera devices...';
  }

  return (
    <div className="align-center flex flex-col">
      <div id="photo-camera-area" ref={outlineRef}>
        {/* SHOW CARD SAFE AREA WHEN TAKING A PHOTO OF A CARD */}
        {props.style === 'card' && (
          <div
            id="photo-camera-card-safe-outline"
            style={{
              width: captureBoxWidth,
              height: captureBoxHeight,
              outline: '5px solid #fff',
              position: 'absolute',
              marginTop: captureBoxMarginTop,
              marginLeft: captureBoxMarginLeft,
              borderRadius: '15px',
            }}
          />
        )}
        <Webcam
          id="photo-camera"
          audio={false}
          ref={webcamRef}
          videoConstraints={props.style === 'card' ? cardVideoConstraints : defaultVideoConstraints}
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
          forceScreenshotSourceSize={true}
        />
      </div>
      <Button
        id="take-photo"
        type="button"
        textContent="Capture photo"
        onClick={capture}
        working={working}
        workingText="Capturing..."
        disabled={working}
      />
    </div>
  );
};
